import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLFormElement> {
    static targets = ["group", "guide"];

    declare readonly groupTargets: HTMLElement[];
    declare readonly guideTarget: HTMLDialogElement;

    private readonly syncAfterAutoRefresh = (): void => {
        this.sync();
    };

    connect(): void {
        document.addEventListener("turbolinks:load", this.syncAfterAutoRefresh);
        this.sync();
    }

    disconnect(): void {
        document.removeEventListener("turbolinks:load", this.syncAfterAutoRefresh);
    }

    sync(): void {
        for (const group of this.groupTargets) {
            const model = group.querySelector<HTMLSelectElement>(
                '[data-agent-model-settings-target="model"]',
            );
            const runtimeFields = group.querySelector<HTMLElement>(
                '[data-agent-model-settings-target="runtimeFields"]',
            );
            const selectedModel = model?.selectedOptions[0];
            const supportsReasoning =
                selectedModel?.dataset.supportsReasoningSettings === "true";
            const supportsSpeed = selectedModel?.dataset.supportsSpeedSettings === "true";
            const wasSupported = group.dataset.codexSettingsActive === "true";
            const reasoning = runtimeFields?.querySelector<HTMLSelectElement>(
                '[data-agent-model-settings-target~="reasoning"]',
            );
            const speed = runtimeFields?.querySelector<HTMLSelectElement>(
                'select[name$="CodexSpeed"]',
            );
            const reasoningUnavailable = group.querySelector<HTMLElement>(
                '[data-agent-model-settings-target~="reasoningUnavailable"]',
            );
            const speedUnavailable = group.querySelector<HTMLElement>(
                '[data-agent-model-settings-target~="speedUnavailable"]',
            );

            if (model && reasoning && supportsReasoning) {
                this.syncSupportedOptions(
                    reasoning,
                    [model.value],
                    selectedModel?.dataset.defaultCodexReasoning ?? "",
                    !wasSupported,
                );
            }

            if (model && speed && supportsSpeed) {
                this.syncSupportedOptions(
                    speed,
                    [model.value],
                    selectedModel?.dataset.defaultCodexSpeed ?? "standard",
                    false,
                );
            }

            if (reasoning) {
                reasoning.disabled = !supportsReasoning;
                reasoning.closest(".field")?.classList.toggle(
                    "opacity-50",
                    !supportsReasoning,
                );
            }
            if (speed) {
                speed.disabled = !supportsSpeed;
                speed.closest(".field")?.classList.toggle(
                    "opacity-50",
                    !supportsSpeed,
                );
            }
            reasoningUnavailable?.classList.toggle(
                "hidden",
                supportsReasoning,
            );
            speedUnavailable?.classList.toggle(
                "hidden",
                supportsSpeed,
            );
            group.dataset.codexSettingsActive = supportsReasoning ? "true" : "false";
        }
    }

    private syncSupportedOptions(
        select: HTMLSelectElement,
        models: string[],
        defaultValue: string,
        forceDefault: boolean,
    ): void {
        let firstSupported = "";
        let currentIsSupported = false;

        for (const option of Array.from(select.options)) {
            const supportedModels = (option.dataset.supportedModels ?? "").split(" ");
            const supported =
                models.length > 0 && models.every((model) => supportedModels.includes(model));
            option.disabled = !supported;
            option.hidden = !supported;
            if (supported && !firstSupported) {
                firstSupported = option.value;
            }
            if (supported && option.value === select.value) {
                currentIsSupported = true;
            }
        }

        if (models.length > 0 && (forceDefault || !currentIsSupported)) {
            const defaultOption = Array.from(select.options).find(
                (option) => option.value === defaultValue && !option.disabled,
            );
            select.value = defaultOption?.value ?? firstSupported;
        }
    }

    openGuide(event: Event): void {
        event.preventDefault();

        if (!this.guideTarget.open) {
            this.guideTarget.showModal();
        }
    }

    closeGuide(event: Event): void {
        event.preventDefault();
        this.guideTarget.close();
    }
}
